import React, { useEffect, useRef, useState } from 'react';
import './Button.scss';
import {
  SessionEndDetails,
  SessionEndPageDetails,
  SetSessionEndRequest
} from '../models/api';
import {
  add as addEndPage,
  remove as removePage,
  updatePages,
  update as updateEndPage
} from '../services/endPage.service';
import { getEnd, updateEnd } from '../services/session.service';
import LoadingSpinner from './LoadingSpinner';
import { ResponseData } from '../services/models';
import Button from './Button';
import Feedback from './Feedback';
import { useRefresh } from '../contexts/RefreshContext';
import { ReactComponent as AddIcon } from '../assets/add_medium.svg';
import { add as addPage } from '../services/page.service';
import {
  DragDropContext,
  Draggable,
  Droppable,
  DropResult
} from '@hello-pangea/dnd';
import { FaGripVertical } from 'react-icons/fa';
import ContentEditor from './ContentEditor';
import PageEditor from './PageEditor';
import { useUnsavedChanges } from '../contexts/UnsavedChangesProvider';
import BackButton from './BackButton';
import { ReactComponent as BackIcon } from '../assets/chevron_backward_large.svg';
import DeleteButton from './DeleteButton';
import EditableLabel from './EditableLabel';
import { ReactComponent as DeleteIcon } from '../assets/delete_large.svg';
import { useAuth } from '../contexts/AuthContext';
import { FeatureGate } from './FeatureGate';

type SessionEndEditorProps = {
  sessionReference: string;
  setMessage: (message: string) => void;
  setErrors: (errors: string[]) => void;
};

const SessionEndEditor: React.FC<SessionEndEditorProps> = ({
  sessionReference,
  setMessage,
  setErrors
}) => {
  const { refreshKey, triggerRefresh } = useRefresh();
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [isDeletingPage, setIsDeletingPage] = useState(false);
  const { hasFeature } = useAuth();

  const [donationPageLink, setDonationPageLink] = useState('');
  const [feedbackLink, setFeedbackLink] = useState('');
  const [subscribeLink, setSubscribeLink] = useState('');
  const [previewLink, setPreviewLink] = useState('');
  const [pages, setPages] = useState<SessionEndPageDetails[]>([]);

  const [isChangingIndex, setIsChangingIndex] = useState(false);

  const getEndData = async (
    input?: ResponseData<SessionEndDetails> | undefined
  ): Promise<boolean> => {
    setLoading(true);

    try {
      const result = input ?? (await getEnd(sessionReference));

      if (result.success && result.data) {
        triggerRefresh();
        setDonationPageLink(
          hasFeature('EndDonationLink')
            ? (result.data.donationPageLink ?? '')
            : ''
        );
        setFeedbackLink(
          hasFeature('EndFeedbackLink') ? (result.data.feedbackLink ?? '') : ''
        );
        setSubscribeLink(
          hasFeature('EndSubscribeLink')
            ? (result.data.subscribeLink ?? '')
            : ''
        );
        setPreviewLink(result.data.previewLink);
        setPages(result.data.pages);
        setPages(hasFeature('EndCustomPages') ? result.data.pages : []);
      } else {
        setErrors(result.errors);
        return false;
      }
      return true;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getEndData();
  }, []);

  const handle = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    setErrors([]);
    setIsSubmitting(true);

    const request: SetSessionEndRequest = {
      donationPageLink,
      feedbackLink,
      subscribeLink
    };

    const result = await updateEnd(sessionReference, request);

    if (result.success && result.data) {
      setMessage('Updated');
      await getEndData();
      setIsSubmitting(false);
      return true;
    } else {
      setIsSubmitting(false);
      setErrors(result.errors);
    }

    return false;
  };

  const addNewPage = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!hasFeature('EndCustomPages')) {
      return;
    }

    setIsAdding(true);
    const page = await addPage({});

    if (page.success && page.data) {
      const newPage = await addEndPage(sessionReference, {
        name: `New Page`,
        pageReference: page.data.reference
      });

      if (newPage.success && newPage.data) {
        const addedPage = {
          endPageReference: newPage.data.reference,
          pageId: page.data.reference,
          name: newPage.data.name ?? ''
        };
        setPages([...pages, addedPage]);
        setSelectedPage(addedPage);
        setMessage('');
        setErrors([]);
        triggerRefresh();
      } else if (page.errors) {
        setErrors(page.errors);
      }
    } else if (page.errors) {
      setErrors(page.errors);
    }
    setIsAdding(false);
  };

  const onDragEnd = async (result: DropResult) => {
    const { destination, source } = result;
    // If there's no destination (outside the list), or if the item is dropped back in the same position, do nothing.
    if (!destination || destination.index === source.index || isChangingIndex) {
      return;
    }

    setIsChangingIndex(true);

    try {
      const newPages = Array.from(pages);
      const [movedPage] = newPages.splice(source.index, 1);
      newPages.splice(destination.index, 0, movedPage);

      setPages(newPages);

      const updatedOrder = newPages.map((page, index) => ({
        reference: page.endPageReference,
        orderIndex: index
      }));

      const response = await updatePages(sessionReference, updatedOrder);

      if (response.success && response.data?.pages) {
        //setPages(response.data?.pages);
        triggerRefresh();
      } else {
        setErrors(response.errors);
      }
    } catch {
      setErrors(['There was a problem updating the order']);
    } finally {
      setIsChangingIndex(false);
    }
  };
  const [selectedPage, setSelectedPage] =
    useState<SessionEndPageDetails | null>(null);
  const { hasUnsavedChanges, setHasUnsavedChanges } = useUnsavedChanges();
  const bypassPromptRef = useRef(false);
  const showPage = (pageDetails: SessionEndPageDetails) => {
    const isSamePage = selectedPage?.pageId === pageDetails.pageId;

    if (hasUnsavedChanges) {
      const confirmed = window.confirm(
        'You have unsaved changes, do you want to discard them?'
      );
      if (!confirmed) {
        return;
      }

      bypassPromptRef.current = true;
      setHasUnsavedChanges(false);
    }

    if (isSamePage) {
      setSelectedPage(null);
    } else {
      setSelectedPage(pageDetails);
    }
  };

  const onDelete = async (selectedPage: SessionEndPageDetails) => {
    setIsDeletingPage(true);

    const result = await removePage(
      sessionReference,
      selectedPage.endPageReference
    );

    if (result.success) {
      bypassPromptRef.current = true;
      setHasUnsavedChanges(false);
      setSelectedPage(null);
      setPages([...pages.filter((p) => p !== selectedPage)]);
      setMessage('Page deleted');
    } else if (result.errors) {
      setErrors(result.errors);
    }

    triggerRefresh();
    setIsDeletingPage(false);
  };

  const saveName = async (
    value: string,
    selectedPage: SessionEndPageDetails
  ) => {
    const result = await updateEndPage(
      sessionReference,
      selectedPage.endPageReference,
      {
        name: value
      }
    );

    if (result.success && result.data) {
      setPages((p) =>
        p.map((q) => {
          if (q.endPageReference === result.data?.reference) {
            return { ...q, name: result.data?.name ?? '' };
          }
          return q;
        })
      );
      setSelectedPage({ ...selectedPage, name: value });
    }

    triggerRefresh();
  };

  const goBack = () => {
    if (hasUnsavedChanges) {
      const confirmed = window.confirm(
        'You have unsaved changes, do you want to discard them?'
      );
      if (!confirmed) {
        return;
      }

      bypassPromptRef.current = true;
      setHasUnsavedChanges(false);
    }
    setSelectedPage(null);
  };

  if (loading) {
    return <LoadingSpinner inline={true} />;
  }

  if (selectedPage) {
    return (
      <>
        <h2>End screen</h2>
        <Button type="Link" onClick={() => goBack()}>
          <BackIcon /> Back
        </Button>
        <p>This page will be available at the end of your event.</p>
        <form className="content-form content-form-inline">
          <EditableLabel
            label={selectedPage.name}
            itemId={selectedPage.endPageReference}
            onSave={(p) => saveName(p, selectedPage)}
          />
          <div className="content-form__full-page-only">
            <DeleteButton
              type="Secondary"
              size="Small"
              isLoading={isDeletingPage}
              onClick={() => onDelete(selectedPage)}
              message="Are you sure you want to delete this page?"
            >
              <DeleteIcon />
            </DeleteButton>
          </div>
        </form>
        <PageEditor
          pageReference={selectedPage.pageId}
          setErrors={setErrors}
          setMessage={setMessage}
        />
      </>
    );
  }

  return (
    <>
      <h2>End screen</h2>
      <p>This screen will show when the event has ended.</p>
      <form onSubmit={handle} className="content-form">
        <FeatureGate feature="EndDonationLink">
          <div className="field">
            <label htmlFor="donationPageLink">Donation Page Link</label>
            <input
              id="donationPageLink"
              type="text"
              placeholder="Donation Page Link"
              value={donationPageLink}
              onChange={(e) => setDonationPageLink(e.target.value)}
            />
          </div>
        </FeatureGate>
        <FeatureGate feature="EndFeedbackLink">
          <div className="field">
            <label htmlFor="feedbackLink">Feedback Link</label>
            <input
              id="feedbackLink"
              type="text"
              placeholder="Feedback Link"
              value={feedbackLink}
              onChange={(e) => setFeedbackLink(e.target.value)}
            />
          </div>
        </FeatureGate>
        <FeatureGate feature="EndSubscribeLink">
          <div className="field">
            <label htmlFor="subscribeLink">Subscribe Link</label>
            <input
              id="subscribeLink"
              type="text"
              placeholder="Subscribe Link"
              value={subscribeLink}
              onChange={(e) => setSubscribeLink(e.target.value)}
            />
          </div>
        </FeatureGate>
        <FeatureGate
          anyFeature={[
            'EndDonationLink',
            'EndFeedbackLink',
            'EndSubscribeLink'
          ]}
        >
          <div className="button-container">
            <Button isLoading={isSubmitting} isSubmit={true}>
              Save
            </Button>
          </div>
        </FeatureGate>
      </form>

      <FeatureGate feature="EndCustomPages">
        <h3>Custom Pages</h3>
        <form onSubmit={addNewPage} className="content-form">
          {pages.length > 0 && (
            <DragDropContext onDragEnd={onDragEnd}>
              <Droppable
                droppableId="endPages"
                isDropDisabled={isChangingIndex}
              >
                {(provided) => (
                  <div
                    className="sections"
                    {...provided.droppableProps}
                    ref={provided.innerRef}
                  >
                    {pages.map((p, index) => (
                      <Draggable
                        key={p.pageId}
                        draggableId={p.pageId}
                        index={index}
                      >
                        {(provided) => (
                          <div
                            id={`slide-${p.pageId}`}
                            key={p.pageId}
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            className={`section draggable`}
                            onClick={() => showPage(p)}
                          >
                            <FaGripVertical />
                            <span>{p.name}</span>
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          )}
          {pages.length === 0 && <p>No custom pages have been added yet.</p>}
          <div className="button-container">
            <Button type="Secondary" isLoading={isAdding} isSubmit={true}>
              <AddIcon /> Add Page
            </Button>
          </div>
        </form>
      </FeatureGate>

      <h3>Preview</h3>

      <div className="preview-content">
        <iframe src={previewLink + '/end'} key={refreshKey} title="Preview" />
      </div>
    </>
  );
};

export default SessionEndEditor;
